import { IconPaperclip } from '@tabler/icons-solidjs'
import { createEffect, For, type JSX, Match, Show, Switch, splitProps } from 'solid-js'
import { AiMarkdown } from './ai-markdown.tsx'
import { Button } from './button.tsx'
import { SparkIcon } from './icons.tsx'
import { EmptyState } from './state.tsx'

export type AssistantMessage =
  | { id: string; role: 'user'; text: string; at: string; files?: string[] }
  | { id: string; role: 'assistant'; text: string; at: string; model?: string }
  /**
   * The assistant proposing a change. It carries NO applied result and no way to express one:
   * `proposal` is rendered by the caller through <AiDraft>, whose confirm button is the only
   * path to a write. There is deliberately no 'assistant-did' variant — see ground rule 2.
   *
   * This contract stays local until a real endpoint exists; sharing an unbuilt route shape
   * through packages/schema would make speculation look authoritative.
   */
  | { id: string; role: 'proposal'; at: string; proposal: JSX.Element }

export interface AiConversationProps {
  messages: AssistantMessage[]
  /** Assistant is composing — renders the thinking row at the end of the list. */
  pending?: boolean
  emptyTitle?: string
  emptyHint?: string
  /** Suggested openers for the empty state. Clicking one calls onSuggestion. */
  suggestions?: string[]
  onSuggestion?: (text: string) => void
}

const timeFormatter = new Intl.DateTimeFormat('uk-UA', { timeStyle: 'short' })
const formatTime = (at: string): string => timeFormatter.format(new Date(at))

export interface AiMessageProps {
  message: AssistantMessage
}

export const AiMessage = (props: AiMessageProps) => {
  const [local] = splitProps(props, ['message'])

  return (
    <Switch>
      <Match when={local.message.role === 'user' && local.message}>
        {(userMessage) => (
          <article class="ml-auto grid max-w-[85%] justify-items-end gap-1">
            <div class="rounded-[0.875rem] bg-surface-sunken px-3.5 py-2.5 text-body text-text">
              <p class="break-words whitespace-pre-wrap">{userMessage().text}</p>
              <Show when={userMessage().files?.length}>
                <ul class="mt-2 grid gap-1">
                  <For each={userMessage().files}>
                    {(file) => (
                      <li class="flex min-w-0 items-center gap-1.5 rounded-full bg-surface px-2 py-1 text-micro text-text-muted">
                        <IconPaperclip
                          size={14}
                          strokeWidth={1.5}
                          class="shrink-0"
                          aria-hidden="true"
                        />
                        <span class="truncate">{file}</span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
            <time class="text-micro text-text-subtle" dateTime={userMessage().at}>
              {formatTime(userMessage().at)}
            </time>
          </article>
        )}
      </Match>

      <Match when={local.message.role === 'assistant' && local.message}>
        {(assistantMessage) => (
          <article class="grid max-w-[95%] grid-cols-[1.75rem_minmax(0,1fr)] items-start gap-2 max-md:max-w-full max-md:grid-cols-1">
            <span class="grid size-7 place-items-center rounded-control bg-accent-soft text-accent max-md:hidden">
              <SparkIcon />
            </span>
            <div class="grid gap-1">
              <span class="text-micro font-semibold text-text-muted">
                АМ-Автопром · помічник
                {assistantMessage().model ? ` · ${assistantMessage().model}` : ''}
              </span>
              <AiMarkdown text={assistantMessage().text} />
              <time class="text-micro text-text-subtle" dateTime={assistantMessage().at}>
                {formatTime(assistantMessage().at)}
              </time>
            </div>
          </article>
        )}
      </Match>

      <Match when={local.message.role === 'proposal' && local.message}>
        {(proposalMessage) => (
          <article class="grid w-full gap-1">
            {proposalMessage().proposal}
            <time class="text-micro text-text-subtle" dateTime={proposalMessage().at}>
              {formatTime(proposalMessage().at)}
            </time>
          </article>
        )}
      </Match>
    </Switch>
  )
}

export const AiThinkingIndicator = () => (
  <div
    class="grid grid-cols-[1.75rem_minmax(0,1fr)] items-center gap-2 text-body text-text-subtle max-md:grid-cols-1"
    aria-live="polite"
  >
    <span class="sr-only">Помічник готує відповідь</span>
    <span class="grid size-7 place-items-center rounded-control bg-accent-soft text-accent max-md:hidden">
      <SparkIcon />
    </span>
    <span class="flex items-center gap-2" aria-hidden="true">
      <span>Аналізую контекст</span>
      <span class="flex gap-1">
        <For each={[0, 1, 2]}>
          {(index) => (
            <span
              class="size-1.5 animate-[pulse-soft_1.4s_var(--ease-in-out)_infinite] rounded-pill bg-current"
              style={{ 'animation-delay': `${index * 160}ms` }}
            />
          )}
        </For>
      </span>
    </span>
  </div>
)

export const AiConversation = (props: AiConversationProps) => {
  const [local] = splitProps(props, [
    'messages',
    'pending',
    'emptyTitle',
    'emptyHint',
    'suggestions',
    'onSuggestion',
  ])
  let log!: HTMLDivElement
  let isNearBottom = true

  createEffect(() => {
    local.messages.at(-1)?.id
    local.pending
    const shouldScroll = isNearBottom

    queueMicrotask(() => {
      if (shouldScroll) log.scrollTop = log.scrollHeight
    })
  })

  return (
    <div
      ref={log}
      class="min-h-0 flex-1 overflow-y-auto p-4"
      role="log"
      aria-live="polite"
      onScroll={(event) => {
        const element = event.currentTarget
        isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight <= 48
      }}
    >
      <Show
        when={local.messages.length > 0}
        fallback={
          <EmptyState
            title={local.emptyTitle ?? 'Розмову ще не розпочато'}
            description={local.emptyHint ?? 'Поставте запитання про роботу на цьому екрані.'}
            action={
              <Show when={local.onSuggestion && local.suggestions?.length}>
                <div class="flex flex-wrap justify-center gap-2">
                  <For each={local.suggestions}>
                    {(suggestion) => (
                      <Button
                        variant="secondary"
                        size="sm"
                        class="rounded-full"
                        onClick={() => local.onSuggestion?.(suggestion)}
                      >
                        {suggestion}
                      </Button>
                    )}
                  </For>
                </div>
              </Show>
            }
          />
        }
      >
        <div class="grid gap-5">
          <For each={local.messages}>{(message) => <AiMessage message={message} />}</For>

          <Show when={local.pending}>
            <AiThinkingIndicator />
          </Show>
        </div>
      </Show>
    </div>
  )
}
